import { useContext } from 'react';
import {
  Heading,
  utils as u,
  Pill,
  enums,
  Icon,
  CopyLink,
  Button,
} from 'edrolo-ui';
import { ProgressButton, StudentProgress } from './components';
import { Attachment, Lesson } from '../../../../../../types/Assemblage';
import { LessonType } from 'edrolo/enums/LessonType';
import urlResolver from 'edrolo/utils/URLResolver';
import { ClassContext, UserContext } from 'edrolo/context';
import s from './styles.scss';
import { Link } from 'react-router-dom';
import { getPillSpecs } from './utils';
import { CardAttachments } from '../CardAttachments';
import { formatDuration } from '../../utils/utils';
import cx from 'classnames';

interface ChunksCardProps {
  lesson: Lesson;
  attachments: Attachment[];
  showStudentNotes: boolean;
}

export const ChunksCard = ({
  lesson,
  attachments,
  showStudentNotes,
}: ChunksCardProps) => {
  const {
    data: { isTeacherView, classId },
  } = useContext(ClassContext);

  const { profile } = useContext(UserContext);

  const url = urlResolver.reverse('chunkview-chunk', {
    lessonId: lesson.id,
    classId,
    index: 1,
  });

  const lessonProgressUrl = urlResolver.reverse('class-lesson-grid', {
    lessonId: lesson.id,
    classId,
  });

  const shortLinkProps = {
    id: lesson.id,
    appLabel: 'tod',
    model: 'lesson',
    queryString: '?',
  };

  const { iconName, pillColor, pillText } = getPillSpecs(lesson.lesson_type);

  return (
    <div
      className={cx(s.chunksCard, {
        [s.highlight]: !lesson.is_published,
      })}
      data-appcues="chunks-card"
    >
      <header>
        <div className={s.titleBlock}>
          <Link
            to={url}
            className={cx({
              [s.disabled]: lesson.is_coming_soon && !profile.is_staff,
            })}
          >
            <Pill color={pillColor} variant={enums.PillVariant.FADE}>
              <Icon name={iconName} className={u.mr1} />
              {pillText}
            </Pill>
          </Link>

          <Link
            to={url}
            className={cx({
              [s.disabled]: lesson.is_coming_soon && !profile.is_staff,
            })}
          >
            <Heading
              level={4}
              className={u.bold}
              data-test="chunks-card-header"
            >
              {lesson.title}
            </Heading>
          </Link>

          {lesson.is_coming_soon ? (
            <Pill color={enums.ColorHue.GREY}>Coming Soon</Pill>
          ) : (
            <>
              <Pill
                variant={enums.PillVariant.OUTLINE}
                color={enums.ColorTheme.FOREGROUND}
              >
                {lesson.lesson_type === LessonType.VIDEO
                  ? formatDuration(lesson.total_duration, !!'isNoun')
                  : `${lesson.chunks.length} Slides`}
              </Pill>
            </>
          )}
          {!lesson.is_published && (
            <Pill color={enums.ColorHue.GREY}>Unpublished</Pill>
          )}
        </div>
        {!lesson.is_coming_soon && (
          <div className={cx(u.flex, u.gap2)}>
            {isTeacherView && (
              <>
                <ProgressButton lesson={lesson} url={lessonProgressUrl} />
                <CopyLink
                  buttonProps={{
                    size: enums.Size.SMALL,
                    variant: enums.Variant.OUTLINE,
                  }}
                  shortLinkProps={shortLinkProps}
                />
              </>
            )}
            {showStudentNotes && (
              <Button.Anchor
                size={enums.Size.SMALL}
                variant={enums.Variant.GHOST}
                color={enums.ColorTheme.SECONDARY}
                href={urlResolver.reverse('student-notes-notebook', {
                  classId,
                  lessonId: lesson.id,
                  profileId: profile.profile_id,
                })}
              >
                <Icon name={enums.Icons.ACTIVITY} className={u.mr2} />
                View notes
              </Button.Anchor>
            )}
            {!isTeacherView && <StudentProgress lesson={lesson} url={url} />}
          </div>
        )}
      </header>
      {attachments.length > 0 && (
        <footer>
          <CardAttachments attachments={attachments} />
        </footer>
      )}
    </div>
  );
};
